import { formatDistance } from 'date-fns';
import { capitalize } from 'lodash';
import { MonitorUp } from 'lucide-react';

import { DashboardCard } from '@/components/modules/dashboard/card';
import { DashboardEmpty } from '@/components/modules/dashboard/empty';
import { DashboardListItem } from '@/components/modules/dashboard/list-item';
import { getShipmentStatusConfig } from '@/components/modules/shipment/table/status-indicator';
import { useCurrentDate } from '@/hooks/current-date-provider';
import { TRACKING_IMPORT_URL, TRACKING_REQUESTS_URL } from '@/lib/constants';
import { getShipmentIdTypeLabel } from '@/lib/models/shipment/utils/get-shipment-id-type';
import { TrackingRequestModel } from '@/lib/models/tracking-request/types';

export interface PendingImportsCardProps {
  trackingRequests: TrackingRequestModel[];
  heightConstraint?: boolean;
}

const PendingImportsCard = ({ trackingRequests, heightConstraint = false }: PendingImportsCardProps) => {
  const currentDate = useCurrentDate();

  const hasItems = trackingRequests.length !== 0;

  return (
    <DashboardCard
      title="Pending imports"
      description="We are still waiting to receive tracking data from the carrier for these shipments. We will keep checking for the next 5 days for you."
      icon={<MonitorUp />}
      ctaText="See all previous imports"
      ctaHref={TRACKING_REQUESTS_URL}
      heightConstraint={heightConstraint}
    >
      {trackingRequests.length ? (
        <ul>
          {trackingRequests.map((shipment) => {
            const { id, lastCheckedAt, createdAt, status } = shipment;
            const title = getShipmentIdTypeLabel(shipment);
            const createdAtString = createdAt
              ? `• Uploaded ${formatDistance(new Date(createdAt), currentDate)} ago`
              : '';
            const checkedAtString = lastCheckedAt
              ? `• Checked ${formatDistance(new Date(lastCheckedAt), currentDate)} ago`
              : '';
            const description = `${capitalize(status)} ${checkedAtString || createdAtString}`;
            const iconConfig = getShipmentStatusConfig(status);

            return (
              <DashboardListItem
                key={id}
                title={title.label}
                asColumn
                icon={<span className={iconConfig.iconClassName}>{iconConfig.icon}</span>}
                description={description}
              />
            );
          })}
        </ul>
      ) : null}
      {!hasItems && (
        <DashboardEmpty title="You have no pending imports" cta="Add a new shipment" href={TRACKING_IMPORT_URL} />
      )}
    </DashboardCard>
  );
};

export { PendingImportsCard };
